import { useCallback, useRef, useState } from 'react';

import DatePicker from '../../../../components/DatePicker';
import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { FormList, FormListFieldError, FormListItem } from '../../../../components/FormList';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { WorkflowSubStepProps } from '../../config';

export interface DismissalEntry {
  dismissalDate: string;
  dismissalDetails: string;
}

export interface JobDismissalFormData {
  isAddingEntry?: boolean;
  hasJobDismissal: boolean | null;
  dismissals: FormListItem<DismissalEntry>[];
  draftDismissal: DismissalEntry;
}

const yesNoOptions = [
  { label: '- Select an option -', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const getEmptyDismissal = (): DismissalEntry => ({
  dismissalDate: '',
  dismissalDetails: '',
});

function JobDismissalPage({ data, fieldErrors, updateData }: WorkflowSubStepProps<JobDismissalFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'job-dismissal-page';

  const today = new Date();
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 5);

  const handleDismissalChange = (value: boolean | null) => {
    if (value !== true) {
      updateData({
        ...data,
        dismissals: [],
        draftDismissal: getEmptyDismissal(),
        hasJobDismissal: value,
        isAddingEntry: false,
      });
    } else {
      updateData({
        ...data,
        hasJobDismissal: value,
      });
    }
  };

  const pendingItemsRef = useRef<FormListItem<DismissalEntry>[] | null>(null);

  const handleItemsChange = useCallback(
    (items: FormListItem<DismissalEntry>[]) => {
      pendingItemsRef.current = items;
      updateData({
        ...data,
        dismissals: items,
      });
    },
    [data, updateData],
  );

  const handleDraftChange = useCallback(
    (draft: DismissalEntry, isDraftActive?: boolean) => {
      const itemsToUse = pendingItemsRef.current !== null ? pendingItemsRef.current : data.dismissals;
      pendingItemsRef.current = null;

      updateData({
        ...data,
        dismissals: itemsToUse,
        draftDismissal: draft,
        isAddingEntry: isDraftActive ?? data.isAddingEntry,
      });
    },
    [data, updateData],
  );

  const handleDraftActiveChange = useCallback(
    (isActive: boolean) => updateData({ ...data, isAddingEntry: isActive }),
    [data, updateData],
  );

  const validateDismissal = useCallback((item: DismissalEntry): FormListFieldError => {
    const errors: FormListFieldError = {};

    if (!item.dismissalDate) {
      errors.dismissalDate = 'Date of the dismissal is required';
    }
    if (!item.dismissalDetails) {
      errors.dismissalDetails = 'Explanation of the dismissal is required';
    }

    return errors;
  }, []);

  const isDraftEmpty = useCallback((draft: DismissalEntry): boolean => {
    return !draft.dismissalDate && !draft.dismissalDetails;
  }, []);

  const renderEditForm = useCallback(
    (item: DismissalEntry, onChange: (item: DismissalEntry) => void, errors: FormListFieldError) => {
      const handleFieldChange = <K extends keyof DismissalEntry>(field: K, value: DismissalEntry[K]) => {
        onChange({
          ...item,
          [field]: value,
        });
      };

      return (
        <div
          className='form-list__nested-form'
          data-testid={`${baseTestId}-entry-form`}>
          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-entry-date`}>
            <DatePicker
              required
              data-testid={`${baseTestId}-entry-date-picker`}
              errorMessage={errors.dismissalDate}
              fromDate={minDate}
              id='dismissalDate'
              label='Date of the dismissal'
              name='dismissalDate'
              toDate={today}
              tooltipText='Enter the date of the dismissal or separation'
              value={item.dismissalDate || ''}
              onDateChange={(date, formattedDate) => handleFieldChange('dismissalDate', formattedDate || '')}
            />
          </div>

          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-entry-details`}>
            <TextField
              multiline
              required
              data-testid={`${baseTestId}-entry-details-field`}
              errorMessage={errors.dismissalDetails}
              label='Explanation of the dismissal'
              name='dismissalDetails'
              rows={5}
              tooltipText='Provide details including the date, an explanation of the problem, reason for leaving, and the employer name and address'
              value={item.dismissalDetails || ''}
              onChange={(e) => handleFieldChange('dismissalDetails', e.target.value)}
            />
          </div>
        </div>
      );
    },
    [minDate, today],
  );

  const renderItemSummary = useCallback((item: DismissalEntry) => {
    const truncatedDetails =
      item.dismissalDetails.length > 50 ? `${item.dismissalDetails.substring(0, 50)}...` : item.dismissalDetails;

    return {
      details: truncatedDetails,
      title: item.dismissalDate,
    };
  }, []);

  const renderReadOnlyItem = useCallback((item: DismissalEntry) => {
    return (
      <div className='form-list__nested-form'>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Date</span>
          <span className='form-list__readonly-value'>{item.dismissalDate}</span>
        </div>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Details</span>
          <span className='form-list__readonly-value'>{item.dismissalDetails}</span>
        </div>
      </div>
    );
  }, []);

  const draftFieldErrors: FormListFieldError = {
    dismissalDate: fieldErrors?.['draftDismissal.dismissalDate'],
    dismissalDetails: fieldErrors?.['draftDismissal.dismissalDetails'],
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-has-job-dismissal`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-has-job-dismissal-dropdown`}
          errorMessage={fieldErrors?.hasJobDismissal}
          label='During the last 5 years, have you ever been fired from a job, left a job due to specific problems, or been debarred from Federal employment for any reason?'
          name='hasJobDismissal'
          options={yesNoOptions}
          tooltipText='This question covers job terminations, resignations in lieu of termination, or debarment from Federal employment within the last 5 years.'
          value={data.hasJobDismissal}
          onChange={handleDismissalChange}
        />
      </div>

      {data.hasJobDismissal === true && (
        <div
          className='ds-u-margin-bottom--3'
          data-testid={`${baseTestId}-entries`}>
          <FormList<DismissalEntry>
            addLinkText='Add Another Dismissal'
            data-testid={`${baseTestId}-entries-list`}
            draftItem={data.draftDismissal}
            fieldErrors={draftFieldErrors}
            getEmptyItem={getEmptyDismissal}
            isDraftActive={data.isAddingEntry}
            isDraftEmpty={isDraftEmpty}
            items={data.dismissals}
            label='Dismissal Details'
            renderEditForm={renderEditForm}
            renderItemSummary={renderItemSummary}
            renderReadOnlyItem={renderReadOnlyItem}
            tooltipText='Provide details about each job dismissal or separation within the last 5 years.'
            validateItem={validateDismissal}
            onDraftActiveChange={handleDraftActiveChange}
            onDraftChange={handleDraftChange}
            onItemsChange={handleItemsChange}
          />
        </div>
      )}

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Job Dismissals'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Federal Employment Details</h3>
          <p>
            This section asks if you&apos;ve been fired, had to resign under pressure, or been banned from Federal jobs
            in the last 5 years. This is required for the Declaration of Federal Employment (OF-306).
          </p>

          <h4 className='ds-u-margin-top--3'>What to Include</h4>
          <ul>
            <li>Being fired from a job.</li>
            <li>Having your employment suddenly ended.</li>
            <li>Leaving a job instead of being fired.</li>
            <li>
              Leaving a job because both the employee and employer agree to part ways after accusations or claims of
              wrongdoing.
            </li>
            <li>Being banned from Federal employment.</li>
          </ul>

          <h4 className='ds-u-margin-top--3'>Date of the dismissal</h4>
          <p>Enter the date of dismissal from the job or the date you left the job.</p>

          <h4 className='ds-u-margin-top--3'>Explanation of the dismissal</h4>
          <p>Enter details about the dismissal. Describe the events that led to it.</p>
          {/* TODO: Restore the following guidance when ACP-791 is completed with these fields.
          <h4 className='ds-u-margin-top--3'>Reason for leaving</h4>
          <p>Enter the reason why you left the job or were dismissed from it.</p>
          <h4 className='ds-u-margin-top--3'>Name of the employer involved</h4>
          <p>Enter the name of the employer that was involved in the dismissal.</p>
          <h4 className='ds-u-margin-top--3'>Employer Address</h4>
          <p>Enter the address of the employer that was involved in the dismissal.</p>

          <ul>
            <li>Address Line 1: Enter the employer's street address.</li>
            <li>Address Line 2: Enter the employer's suite, floor, or building number (if this applies).</li>
            <li>City: Enter the city name of the employer.</li>
            <li>State: Select the employer's state from the dropdown.</li>
            <li>Zip Code: Enter the employer's 5-digit ZIP code.</li>
          </ul>
          */}
          <p className='ds-u-margin-top--3'>
            <strong>Note:</strong> If you have more than one dismissal to detail, use the &quot;Add Another
            Dismissal&quot; button to add each one.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default JobDismissalPage;
